import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Dropdown,
  Menu,
  Input,
  Space,
  Popover,
  Skeleton,
  Select,
  Tag,
  message,
  Segmented,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  DownloadOutlined,
  SettingOutlined,
  ClearOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchRFIs,
  deleteRFI,
  updateRFI,
  archiveRFI,
  downloadRFICoversheet,
  notifyProjectUsersAboutRFI,
  getRfiDefaultNotifyRecipients,
} from "../services/rfisAPI";
import { useAuth } from "../hooks/useAuth";
import CreateRFIModal from "../components/CreateRFIModal";
import UpdateRFIModal from "../components/UpdateRFIModal";
import ViewRFIModal from "../components/ViewRFIModal";
import NotifyUsersModal from "../components/NotifyUsersModal";
import Highlighter from "react-highlight-words";
import { exportRFIsToPDF, getRFIPdfColumnOptions } from "../utils/pdfUtils";
import ExportColumnsModal from "../components/ExportColumnsModal";
import { formatDate } from "../utils/dateUtils";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { fetchCurrentUser, updateUser } from "../services/authAPI";
import SortableHeader from "../components/SortableHeader";
import ColumnSelectorPopover from "../components/ColumnSelectorPopover";
import {
  buildFullColumnOrder,
  getVisibleColumnsFromOrder,
  normalizeColumnSelectionOrder,
  orderColumnsByKeys,
  pinKeyLast,
} from "../utils/columnOrderUtils";
import {
  getPreferredPageSize,
  savePreferredPageSize,
} from "../utils/paginationUtils";
import { PAYMENT_PROJECTS_LIST } from "../constants/paymentConstants";
import {
  getModuleSettings,
  saveModuleSettings,
  clearModuleSettings,
} from "../utils/filterStorageUtils";
import {
  applyDateFiltersToQueryParams,
  createGetColumnDateRangeProps,
} from "../utils/dateFilterUtils";
import QuickFilterButtons from "../components/QuickFilterButtons";
import DueDatePill from "../components/DueDatePill";
import { toggleQuickFilter } from "../utils/quickFilterUtils";

const RFI_COLUMN_OPTIONS = [
  { key: "number", label: "Number" },
  { key: "title", label: "Title" },
  { key: "formatted_assignees", label: "Assignees" },
  { key: "formatted_ball_in_court_users", label: "Ball in Court" },
  { key: "formatted_distribution_list", label: "CCs" },
  { key: "formatted_spec_section", label: "Spec Section" },
  { key: "created_at", label: "Created Date" },
  { key: "due_date", label: "Due Date" },
  { key: "status", label: "Status" },
  { key: "closed_at_date", label: "Closed Date" },
];

const CUSTOM_RFI_PDF_EXPORT_USER_ID = "69f91c5d05da08025dafa92b";
const CUSTOM_RFI_PDF_EXPORT_COLUMNS = [
  "number",
  "title",
  "formatted_ball_in_court_users",
  "sent_date",
  "due_date",
  "status",
  "most_recent_response_date",
];

const RFI_DATE_FILTER_KEYS = ["created_at", "due_date", "closed_at_date"];

const buildRFIFiltersForAPI = (filters) =>
  applyDateFiltersToQueryParams(filters, RFI_DATE_FILTER_KEYS);

const RFIs = () => {
  const {
    selectedProject,
    setSelectedProject,
    magicLoginActive,
    logout,
    currentUser,
    setCurrentUser,
  } = useAuth();
  const projectId = selectedProject.project_id;
  const projectName = selectedProject.project_name;
  const userRole = selectedProject.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  // Field can create/update RFIs but cannot delete them.
  const canCreateOrUpdateRFI = isOwnerOrManager || userRole === "field";
  const canDeleteRFI = isOwnerOrManager;
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isExportPDFModalVisible, setIsExportPDFModalVisible] = useState(false);
  const [pdfExportColumnOptions, setPdfExportColumnOptions] = useState([]);
  const [selectedRFI, setSelectedRFI] = useState(null);
  const [isNotifyUsersModalVisible, setIsNotifyUsersModalVisible] =
    useState(false);
  const [rfiToNotify, setRfiToNotify] = useState(null);
  const [notifyDefaultUserIds, setNotifyDefaultUserIds] = useState([]);
  const [notifyDefaultUserIdsLoading, setNotifyDefaultUserIdsLoading] =
    useState(false);
  const notifyDefaultRecipientsFetchSeqRef = useRef(0);
  const [notifyingRfiId, setNotifyingRfiId] = useState(null);
  const queryClient = useQueryClient();
  const savedSettings = getModuleSettings(projectId, "rfis");
  const [searchedColumns, setSearchedColumns] = useState(
    savedSettings.searchedColumns || [],
  );
  const [filters, setFilters] = useState(savedSettings.filters || {});
  const [searchTexts, setSearchTexts] = useState(
    savedSettings.searchTexts || {},
  );
  const [masterSearch, setMasterSearch] = useState("");
  const [archivedFilter, setArchivedFilter] = useState("active");
  const searchInput = useRef(null);
  const isMagicLogin = localStorage.getItem("login_type") === "magic_login";
  const location = useLocation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("rfis"),
    total: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const rfiId = searchParams.get("rfi_id");
  const projectIdFromUrl = searchParams.get("project_id");
  const magicEmailDeepLinkConsumedRef = useRef(false);
  const userProjectIds =
    currentUser?.projects?.map((p) => p.project_id).join(",") ?? "";
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [lastSavedColumns, setLastSavedColumns] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const lastLoadedProjectId = useRef(projectId);
  const [loadedSettingsProjectId, setLoadedSettingsProjectId] =
    useState(projectId);
  const [sortField, setSortField] = useState(savedSettings.sortField || null);
  const [sortOrder, setSortOrder] = useState(savedSettings.sortOrder || "asc");
  // Helper function to get workflow-aware columns based on user preferences
  const getWorkflowAwareColumns = (userColumns, workflowType) => {
    const masterOrder =
      workflowType === "Sequential"
        ? RFI_COLUMN_OPTIONS.filter(
            (column) => column.key !== "formatted_assignees",
          ).map((column) => column.key)
        : RFI_COLUMN_OPTIONS.map((column) => column.key);

    if (!userColumns || userColumns.length === 0) {
      // Default columns for new users (includes Ball in Court until they hide it)
      return [
        "number",
        "title",
        ...(workflowType !== "Sequential" ? ["formatted_assignees"] : []),
        "formatted_ball_in_court_users",
        "created_at",
        "due_date",
        "status",
      ];
    }

    // Respect saved preferences — do not re-inject columns the user chose to hide
    let adjustedColumns = [...userColumns];

    if (workflowType === "Sequential") {
      adjustedColumns = adjustedColumns.filter(
        (col) => col !== "formatted_assignees",
      );
    }

    return normalizeColumnSelectionOrder(adjustedColumns, masterOrder);
  };

  const [visibleColumns, setVisibleColumns] = useState(
    getWorkflowAwareColumns(
      currentUser?.rfi_columns,
      selectedProject?.rfi_workflow_type,
    ),
  );

  // Check if user has previously customized columns
  useEffect(() => {
    if (currentUser?.rfi_columns && currentUser.rfi_columns.length > 0) {
      setLastSavedColumns(
        getWorkflowAwareColumns(
          currentUser.rfi_columns,
          selectedProject?.rfi_workflow_type,
        ),
      );
    }
  }, [currentUser?.rfi_columns, selectedProject?.rfi_workflow_type]);
  const [tempSelection, setTempSelection] = useState(visibleColumns);
  const [tempOrderedColumns, setTempOrderedColumns] = useState(visibleColumns);
  const getColumnDateRangeProps = createGetColumnDateRangeProps(
    filters,
    setFilters,
  );

  const {
    data: rfiData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "rfis",
      projectId,
      pagination.current,
      pagination.pageSize,
      buildRFIFiltersForAPI(filters),
      masterSearch,
      sortField,
      sortOrder,
      archivedFilter,
    ],

    queryFn: () =>
      fetchRFIs(
        projectId,
        pagination.current,
        pagination.pageSize,
        buildRFIFiltersForAPI(filters),
        masterSearch,
        sortField,
        sortOrder,
        false,
        archivedFilter,
      ),
    enabled: !!projectId && loadedSettingsProjectId === projectId,
    keepPreviousData: true,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      total: undefined,
    }));
  }, [filters, masterSearch, archivedFilter]);

  useEffect(() => {
    if (lastLoadedProjectId.current === projectId) {
      saveModuleSettings(projectId, "rfis", {
        filters,
        searchTexts,
        searchedColumns,
        sortField,
        sortOrder,
      });
    }
  }, [filters, searchTexts, searchedColumns, sortField, sortOrder, projectId]);

  // Load settings when projectId changes
  useEffect(() => {
    const settings = getModuleSettings(projectId, "rfis");
    setFilters(settings.filters || {});
    setSearchTexts(settings.searchTexts || {});
    setSearchedColumns(settings.searchedColumns || []);
    setSortField(settings.sortField || null);
    setSortOrder(settings.sortOrder || "asc");
    setPagination((prev) => ({ ...prev, current: 1 }));
    lastLoadedProjectId.current = projectId;
    setLoadedSettingsProjectId(projectId);
  }, [projectId]);

  // Update visible columns when project workflow type changes
  useEffect(() => {
    if (selectedProject?.rfi_workflow_type) {
      // Use last saved columns if available, otherwise use current user columns
      const columnsToUse = lastSavedColumns || currentUser?.rfi_columns;
      // Process columns based on user preferences and workflow type
      const newColumns = getWorkflowAwareColumns(
        columnsToUse,
        selectedProject.rfi_workflow_type,
      );
      setVisibleColumns(newColumns);
      setTempSelection(newColumns);
    }
  }, [
    selectedProject?.rfi_workflow_type,
    currentUser?.rfi_columns,
    lastSavedColumns,
  ]);

  const openExportPDFModal = () => {
    if (!rfiData || rfiData.total === 0) {
      notification.warning({ message: "No data to export" });
      return;
    }
    setPdfExportColumnOptions(
      getRFIPdfColumnOptions(
        buildFullColumnOrder(visibleColumns, getSelectableColumnKeys()),
        visibleColumns,
      ),
    );
    setIsExportPDFModalVisible(true);
  };

  const handleExportPDF = async (exportColumns) => {
    setIsExportPDFModalVisible(false);
    try {
      if (!rfiData || rfiData.total === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({
        message: "Exporting PDF...",
        description: "Fetching all records...",
        duration: 2,
      });
      const allRFIsData = await fetchRFIs(
        projectId,
        1,
        rfiData.total,
        buildRFIFiltersForAPI(filters),
        masterSearch,
        sortField,
        sortOrder,
        true, // is_export_pdf = true for PDF export
        archivedFilter,
      );

      await exportRFIsToPDF(allRFIsData.rfis, projectName, selectedProject, {
        columnOrder: visibleColumns,
        exportColumns,
      });
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    }
  };

  const handleCustomExportPDF = async () => {
    try {
      if (!rfiData || rfiData.total === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({
        message: "Exporting PDF...",
        description: "Fetching all records...",
        duration: 2,
      });

      const allRFIsData = await fetchRFIs(
        projectId,
        1,
        rfiData.total,
        buildRFIFiltersForAPI(filters),
        masterSearch,
        sortField,
        sortOrder,
        true,
        archivedFilter,
      );

      // Custom export uses the fixed CUSTOM_RFI_PDF_EXPORT_COLUMNS layout, so
      // it must NOT pass the UI columnOrder (visibleColumns) — that would drop
      // required columns (sent_date, most_recent_response_date) and inject
      // UI-only columns.
      await exportRFIsToPDF(allRFIsData.rfis, projectName, selectedProject, {
        columns: CUSTOM_RFI_PDF_EXPORT_COLUMNS,
      });
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    }
  };

  const columnSetMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: async (data, variables) => {
      const user = await fetchCurrentUser();
      localStorage.setItem("user", JSON.stringify(user));
      setCurrentUser(user);
      const nextColumns = getWorkflowAwareColumns(
        variables.rfi_columns,
        selectedProject?.rfi_workflow_type,
      );
      setLastSavedColumns(nextColumns);
      setVisibleColumns(nextColumns);
      setTempSelection(nextColumns);
      setIsPopoverOpen(false);
      setIsLoadingColumns(false);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update columns",
        description: error.message,
      });
      setIsLoadingColumns(false);
    },
  });

  const getSelectableColumnKeys = () => {
    if (selectedProject?.rfi_workflow_type === "Sequential") {
      return RFI_COLUMN_OPTIONS.filter(
        (column) => column.key !== "formatted_assignees",
      ).map((column) => column.key);
    }
    return RFI_COLUMN_OPTIONS.map((column) => column.key);
  };

  const initializeColumnSelector = () => {
    const masterOrder = getSelectableColumnKeys();
    setTempSelection(visibleColumns);
    setTempOrderedColumns(buildFullColumnOrder(visibleColumns, masterOrder));
  };

  const handleSave = () => {
    setIsLoadingColumns(true);

    const columnsToSave = normalizeColumnSelectionOrder(
      getVisibleColumnsFromOrder(tempOrderedColumns, tempSelection),
      getSelectableColumnKeys(),
    );

    columnSetMutation.mutate({
      ...currentUser,
      rfi_columns: columnsToSave,
    });
  };

  const handleCancel = () => {
    setTempSelection(visibleColumns);
    setTempOrderedColumns(
      buildFullColumnOrder(visibleColumns, getSelectableColumnKeys()),
    );
    setIsPopoverOpen(false);
  };

  const deleteMutation = useMutation({
    mutationFn: deleteRFI,
    onSuccess: () => {
      queryClient.invalidateQueries(["rfis", projectId]);
      notification.success({ message: "RFI deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete RFI",
        description: error.message,
      });
    },
  });

  if (isError) {
    notification.error({
      message: "Failed to load RFIs",
      description: error.message,
    });
  }

  const handleUpdateClick = (rfi) => {
    setSelectedRFI(rfi);
    setIsUpdateModalVisible(true);
  };

  const handleViewClick = (rfi) => {
    setSelectedRFI(rfi);
    setIsViewModalVisible(true);
  };

  const handleDeleteClick = (rfiId) => {
    deleteMutation.mutate(rfiId);
  };

  const handleNotifyUsers = async (rfi) => {
    const fetchSeq = ++notifyDefaultRecipientsFetchSeqRef.current;

    setRfiToNotify(rfi);
    setNotifyDefaultUserIds([]);
    setNotifyDefaultUserIdsLoading(true);
    setIsNotifyUsersModalVisible(true);

    try {
      const userIds = await getRfiDefaultNotifyRecipients(rfi.id);
      if (fetchSeq !== notifyDefaultRecipientsFetchSeqRef.current) return;
      setNotifyDefaultUserIds(userIds);
    } catch {
      if (fetchSeq !== notifyDefaultRecipientsFetchSeqRef.current) return;
      setNotifyDefaultUserIds([]);
    } finally {
      if (fetchSeq === notifyDefaultRecipientsFetchSeqRef.current) {
        setNotifyDefaultUserIdsLoading(false);
      }
    }
  };

  const handleSendNotification = async (userIds) => {
    if (!rfiToNotify) return;

    setNotifyingRfiId(rfiToNotify.id);
    setIsNotifyUsersModalVisible(false);

    try {
      const result = await notifyProjectUsersAboutRFI(rfiToNotify.id, userIds);
      notification.success({
        message: "Notification sent successfully",
        description: `Email sent to ${result.recipients_count} project user${result.recipients_count !== 1 ? "s" : ""}`,
      });
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          try {
            const errorData = JSON.parse(error.response.data);
            errorMessage =
              errorData?.detail?.message || errorData?.detail || errorMessage;
          } catch {
            errorMessage = error.response.data;
          }
        } else {
          errorMessage =
            error.response.data?.detail?.message ||
            error.response.data?.detail ||
            errorMessage;
        }
      }

      notification.error({
        message: "Failed to send notification",
        description: errorMessage,
      });
    } finally {
      setNotifyingRfiId(null);
      setRfiToNotify(null);
    }
  };

  const updateStatusMutation = useMutation({
    mutationFn: ({ rfiId, status }) => updateRFI(rfiId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(["rfis", projectId]);
      notification.success({ message: "Status updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleStatusChange = (rfiId, newStatus) => {
    updateStatusMutation.mutate({ rfiId, status: newStatus });
  };

  const archiveMutation = useMutation({
    mutationFn: ({ rfiId, archived }) => archiveRFI(rfiId, archived),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(["rfis", projectId]);
      notification.success({
        message: variables.archived ? "RFI archived" : "RFI unarchived",
      });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update archive status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleArchiveToggle = (rfiId, archived) => {
    archiveMutation.mutate({ rfiId, archived });
  };

  const handleDownloadCoversheet = async (rfi) => {
    const key = "downloadingRFI";
    try {
      message.loading({
        content: "Requesting RFI Summary PDF...",
        key,
        duration: 0,
      });

      const result = await downloadRFICoversheet(rfi.id);

      message.destroy(key);
      notification.info({
        message:
          result?.message ||
          "The report is being prepared. We will email it to you when it is ready.",
      });
    } catch (error) {
      console.error(error);
      message.destroy(key);
      const detail = error.response?.data?.detail;
      notification.error({
        message: "Failed to request RFI Summary PDF",
        description:
          (typeof detail === "string" ? detail : detail?.message) ||
          "Failed to request RFI Summary PDF",
      });
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    if (!searchedColumns.includes(dataIndex)) {
      setSearchedColumns([...searchedColumns, dataIndex]);
    }
    setSearchTexts((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
    setFilters((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    setSearchedColumns((prev) => prev.filter((col) => col !== dataIndex));
    clearFilters();
    confirm();
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
    setSearchTexts((prev) => {
      const updated = { ...prev };
      delete updated[dataIndex];
      return updated;
    });
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, confirm, dataIndex)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text) =>
      searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchTexts[dataIndex] || ""]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const getColumnMultiSelectProps = (dataIndex, options) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      const FilterDropdownContent = () => {
        const [isDropdownOpen, setIsDropdownOpen] = useState(false);

        const handleConfirm = () => {
          confirm();
          setFilters((prev) => ({
            ...prev,
            [dataIndex]: selectedKeys,
          }));
        };

        return (
          <div
            style={{
              padding: 8,
              minWidth: 200,
              maxWidth: 300,
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Select
              mode="multiple"
              placeholder={`Select ${dataIndex}`}
              value={selectedKeys}
              onChange={(values) => setSelectedKeys(values)}
              style={{
                width: "100%",
                marginBottom: 8,
              }}
              options={options}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+${omittedValues.length} more`
              }
              dropdownStyle={{
                maxWidth: 300,
              }}
              onOpenChange={(open) => setIsDropdownOpen(open)}
              suffixIcon={
                isDropdownOpen ? (
                  <UpOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirm();
                    }}
                    style={{
                      cursor: "pointer",
                      color: "#1677ff",
                    }}
                  />
                ) : (
                  <DownOutlined
                    style={{
                      color: "#1677ff",
                    }}
                  />
                )
              }
            />
            <Space>
              <Button
                type="primary"
                onClick={handleConfirm}
                size="small"
                style={{
                  width: 90,
                }}
              >
                OK
              </Button>
              <Button
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm();
                  setFilters((prev) => {
                    const newFilters = { ...prev };
                    delete newFilters[dataIndex];
                    return newFilters;
                  });
                }}
                size="small"
                style={{
                  width: 90,
                }}
              >
                Reset
              </Button>
            </Space>
          </div>
        );
      };

      return <FilterDropdownContent />;
    },
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      const cellValue = record[dataIndex];
      if (cellValue !== undefined && cellValue !== null) {
        return cellValue.toString() === value;
      }
      return false;
    },
    filteredValue: filters[dataIndex] || null,
  });

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, "rfis");
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setSearchTexts({});
    setSearchedColumns([]);
    setMasterSearch("");
    setSortField(null);
    setSortOrder("asc");
    setTableKey((prev) => prev + 1);
    clearModuleSettings(projectId, "rfis");
  };

  const handleQuickFilterToggle = (filterKey, kind) => {
    setFilters((prev) => toggleQuickFilter(prev, filterKey, kind));
    setPagination((prev) => ({ ...prev, current: 1 }));
    setTableKey((prev) => prev + 1);
  };

  const handleSort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    if (!projectIdFromUrl || !currentUser?.projects?.length) return;
    if (selectedProject?.project_id === projectIdFromUrl) return;
    const match = currentUser.projects.find(
      (p) => p.project_id === projectIdFromUrl,
    );
    if (match) {
      setSelectedProject(match);
    }
  }, [
    projectIdFromUrl,
    userProjectIds,
    selectedProject?.project_id,
    setSelectedProject,
  ]);

  useEffect(() => {
    if (rfiId) {
      const rfi = rfiData?.rfis.find((rfi) => rfi.id === rfiId);
      if (rfi) {
        setSelectedRFI(rfi);
        setIsViewModalVisible(true);
        magicEmailDeepLinkConsumedRef.current = true;
        const next = new URLSearchParams(searchParams);
        next.delete("rfi_id");
        next.delete("project_id");
        setSearchParams(next, { replace: true });
      }
    }
  }, [rfiData, rfiId, searchParams, setSearchParams]);

  useEffect(() => {
    const isMagic = magicLoginActive || isMagicLogin;
    if (
      isMagic &&
      location.pathname === "/rfis" &&
      !rfiId &&
      rfiData?.rfis?.length > 0 &&
      !magicEmailDeepLinkConsumedRef.current
    ) {
      notification.warning({
        message: "Access Restricted",
        description:
          "Your access is limited when using mail login link. Please log in normally for full access..",
      });
      logout();
      navigate("/login");
    }
  }, [
    magicLoginActive,
    isMagicLogin,
    rfiId,
    rfiData,
    location.pathname,
    navigate,
  ]);

  const columns = [
    {
      title: (
        <SortableHeader
          title="Number"
          sortField="number"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "number",
      key: "number",
      isVisible: visibleColumns.includes("number"),
      ...getColumnSearchProps("number"),
    },
    {
      title: (
        <SortableHeader
          title="Title"
          sortField="title"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "title",
      key: "title",
      isVisible: visibleColumns.includes("title"),
      ...getColumnSearchProps("title"),
    },
    {
      title: "Assignees",
      dataIndex: "formatted_assignees",
      key: "formatted_assignees",
      width: 300,
      isVisible:
        visibleColumns.includes("formatted_assignees") &&
        selectedProject?.rfi_workflow_type !== "Sequential",
      ...getColumnSearchProps("formatted_assignees"),
    },
    {
      title: "Ball in Court",
      dataIndex: "formatted_ball_in_court_users",
      key: "formatted_ball_in_court_users",
      width: 300,
      isVisible: visibleColumns.includes("formatted_ball_in_court_users"),
      ...getColumnSearchProps("formatted_ball_in_court_users"),
    },
    {
      title: "CCs",
      dataIndex: "formatted_distribution_list",
      key: "formatted_distribution_list",
      width: 300,
      isVisible: visibleColumns.includes("formatted_distribution_list"),
    },
    {
      title: (
        <SortableHeader
          title="Spec Section"
          sortField="formatted_spec_section"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "formatted_spec_section",
      key: "formatted_spec_section",
      isVisible: visibleColumns.includes("formatted_spec_section"),
      ...getColumnSearchProps("formatted_spec_section"),
    },
    {
      title: (
        <SortableHeader
          title="Created Date"
          sortField="created_at"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "created_at",
      key: "created_at",
      isVisible: visibleColumns.includes("created_at"),
      ...getColumnDateRangeProps("created_at", "Created Date"),
      render: (text) => formatDate(text),
    },
    {
      title: (
        <SortableHeader
          title="Due Date"
          sortField="due_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "due_date",
      key: "due_date",
      isVisible: visibleColumns.includes("due_date"),
      ...getColumnDateRangeProps("due_date", "Due Date"),
      render: (text, record) => (
        <DueDatePill dateString={text} status={record.status} />
      ),
    },
    {
      title: (
        <SortableHeader
          title="Status"
          sortField="status"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "status",
      key: "status",
      isVisible: visibleColumns.includes("status"),
      ...getColumnMultiSelectProps("status", [
        { label: "Open", value: "Open" },
        { label: "Closed", value: "Closed" },
        { label: "Draft", value: "Draft" },
      ]),
      render: (status, record) => {
        const getStatusColor = (status) => {
          switch (status) {
            case "Open":
              return "orange";
            case "Closed":
              return "default";
            case "Draft":
              return "blue";
            default:
              return "default";
          }
        };

        // Consultants and subcontractors can only view status, not change it
        if (!canCreateOrUpdateRFI) {
          return (
            <Space size={4}>
              <Tag color={getStatusColor(status)} className="m-0">
                {status}
              </Tag>
              {record.archived && <Tag className="m-0">Archived</Tag>}
            </Space>
          );
        }

        // Owners, managers, and field can change status
        return (
          <Space size={4}>
            <Select
              value={status}
              onChange={(value) => handleStatusChange(record.id, value)}
              className="status-select-tag w-[120px]"
              loading={updateStatusMutation.isPending}
              disabled={updateStatusMutation.isPending}
              popupRender={(menu) => menu}
              suffixIcon={<span className="text-xs text-gray-400 ml-1">▼</span>}
            >
              <Select.Option value="Open">
                <Tag color="orange" className="m-0 w-full text-center block">
                  Open
                </Tag>
              </Select.Option>
              <Select.Option value="Closed">
                <Tag color="default" className="m-0 w-full text-center block">
                  Closed
                </Tag>
              </Select.Option>
              <Select.Option value="Draft">
                <Tag color="blue" className="m-0 w-full text-center block">
                  Draft
                </Tag>
              </Select.Option>
            </Select>
            {record.archived && <Tag className="m-0">Archived</Tag>}
          </Space>
        );
      },
    },
    {
      title: (
        <SortableHeader
          title="Closed Date"
          sortField="closed_at_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "closed_at_date",
      key: "closed_at_date",
      isVisible: visibleColumns.includes("closed_at_date"),
      ...getColumnDateRangeProps("closed_at_date", "Closed Date"),
      render: (text, record) => {
        if (record.status !== "Closed" || !text) {
          return "";
        }
        return formatDate(text);
      },
    },
    {
      title: "Action",
      key: "action",
      isVisible: true,
      render: (_, record) => {
        if (!canCreateOrUpdateRFI) {
          const ballInCourtIds = record.ball_in_court_ids || [];
          const assignees = record.assignees || [];
          const isInBallInCourt = ballInCourtIds.some(
            (id) => String(id) === String(currentUser?.id),
          );
          const isAssignee = assignees.some(
            (assignee) =>
              String(assignee?.id || assignee) === String(currentUser?.id),
          );
          const isRFIManager =
            String(record.rfi_manager_id) === String(currentUser?.id);
          const buttonText =
            isInBallInCourt || isAssignee || isRFIManager ? "Respond" : "View";
          const isRespond = isInBallInCourt || isAssignee || isRFIManager;

          return (
            <Space size="small">
              <Button
                onClick={() => handleViewClick(record)}
                size="medium"
                style={{ width: "90px" }}
                type={isRespond ? "primary" : "default"}
                ghost={isRespond}
              >
                {buttonText}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                size="medium"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadCoversheet(record);
                }}
                title="Download RFI Summary"
              />
            </Space>
          );
        }

        // Owners/managers: full actions. Field: create/update only (no notify/archive/delete).
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="1" onClick={() => handleViewClick(record)}>
                  <Button className="w-full">View</Button>
                </Menu.Item>
                <Menu.Item key="2" onClick={() => handleUpdateClick(record)}>
                  <Button className="w-full">Update</Button>
                </Menu.Item>
                <Menu.Item
                  key="3"
                  onClick={() => handleDownloadCoversheet(record)}
                >
                  <Button className="w-full">Download</Button>
                </Menu.Item>
                {isOwnerOrManager && (
                  <Menu.Item
                    key="notify"
                    onClick={() => handleNotifyUsers(record)}
                    disabled={notifyingRfiId === record.id}
                  >
                    <Button
                      className="w-full"
                      loading={notifyingRfiId === record.id}
                    >
                      Notify Users
                    </Button>
                  </Menu.Item>
                )}
                {isOwnerOrManager && (
                  <Menu.Item
                    key="archive"
                    onClick={() =>
                      handleArchiveToggle(record.id, !record.archived)
                    }
                  >
                    <Tooltip
                      title={
                        record.archived
                          ? "Unarchiving shows this RFI in the list again."
                          : "Archiving hides this RFI from the list and exports. Switch the Active/Archived/All filter to see it."
                      }
                    >
                      <Button className="w-full">
                        {record.archived ? "Unarchive" : "Archive"}
                      </Button>
                    </Tooltip>
                  </Menu.Item>
                )}
                {canDeleteRFI && (
                  <Menu.Item key="4">
                    <Popconfirm
                      title="Are you sure you want to delete this RFI?"
                      onConfirm={() => handleDeleteClick(record.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button className="w-full" danger>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Menu.Item>
                )}
              </Menu>
            }
            trigger={["click"]}
          >
            <Button icon={<EditOutlined />} size="medium"></Button>
          </Dropdown>
        );
      },
    },
  ];

  const selectableColumnOptions = RFI_COLUMN_OPTIONS.filter((column) => {
    if (
      selectedProject?.rfi_workflow_type === "Sequential" &&
      column.key === "formatted_assignees"
    ) {
      return false;
    }
    return true;
  });

  const content = (
    <ColumnSelectorPopover
      columnOptions={selectableColumnOptions}
      orderedKeys={tempOrderedColumns}
      selectedKeys={tempSelection}
      onOrderedKeysChange={setTempOrderedColumns}
      onSelectedKeysChange={setTempSelection}
      onSave={handleSave}
      onCancel={handleCancel}
      allowReorder={true}
    />
  );

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">RFIs</h1>
      <div className="flex gap-4 mb-4">
        {canCreateOrUpdateRFI && (
          <Button
            type="primary"
            onClick={() => {
              if (PAYMENT_PROJECTS_LIST.includes(projectId)) {
                notification.warning({
                  message: "Your project has outstanding invoices.",
                  description:
                    "Your existing data will remain available. To create new RFIs, please have your project administrator complete the payment.",
                });
                return;
              }
              setIsCreateModalVisible(true);
            }}
          >
            Create
          </Button>
        )}
        <QuickFilterButtons
          filters={filters}
          onToggle={handleQuickFilterToggle}
        />
        <Button
          icon={<DownloadOutlined />}
          onClick={openExportPDFModal}
        >
          Export PDF
        </Button>
        {String(currentUser?.id) === CUSTOM_RFI_PDF_EXPORT_USER_ID && (
          <Button
            icon={<DownloadOutlined />}
            onClick={handleCustomExportPDF}
            className="ml-2"
          >
            Custom Export PDF
          </Button>
        )}
        <Popover
          content={isLoadingColumns ? <Skeleton active /> : content}
          title="Select Columns to Display"
          trigger="click"
          open={isPopoverOpen}
          onOpenChange={(visible) => {
            setIsPopoverOpen(visible);
            if (visible) {
              initializeColumnSelector();
            }
          }}
        >
          <Button icon={<SettingOutlined />}>Columns</Button>
        </Popover>
        <Input.Search
          placeholder="Search across all fields..."
          defaultValue=""
          onSearch={(value) => setMasterSearch(value)}
          style={{ width: 400 }}
          allowClear
          enterButton={
            <Button
              type="primary"
              icon={<SearchOutlined />}
              className="!inline-flex !items-center !justify-center"
            />
          }
        />
        <Segmented
          className="ml-auto"
          value={archivedFilter}
          onChange={setArchivedFilter}
          options={[
            { label: "Active", value: "active" },
            { label: "Archived", value: "archived" },
            { label: "All", value: "all" },
          ]}
        />
        <Button
          icon={<ClearOutlined />}
          onClick={handleClearAllFilters}
        >
          Clear All Filters
        </Button>
      </div>

      <Table
        columns={orderColumnsByKeys(
          columns,
          pinKeyLast(visibleColumns, "action"),
        ).filter((col) => col.isVisible === true)}
        dataSource={rfiData?.rfis}
        rowKey="id"
        key={tableKey}
        loading={isLoading}
        rowClassName={(record) => (record.archived ? "opacity-60" : "")}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: rfiData?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />
      <CreateRFIModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
      />
      <ExportColumnsModal
        open={isExportPDFModalVisible}
        columnOptions={pdfExportColumnOptions}
        onCancel={() => setIsExportPDFModalVisible(false)}
        onExport={handleExportPDF}
      />
      {selectedRFI && (
        <UpdateRFIModal
          visible={isUpdateModalVisible}
          onClose={() => {
            setIsUpdateModalVisible(false);
            setSelectedRFI(null);
          }}
          rfiId={selectedRFI?.id}
        />
      )}
      {selectedRFI && (
        <ViewRFIModal
          visible={isViewModalVisible}
          onClose={
            magicLoginActive || isMagicLogin
              ? () => {}
              : () => setIsViewModalVisible(false)
          }
          rfiId={selectedRFI?.id}
          isLocked={magicLoginActive || isMagicLogin}
        />
      )}
      <NotifyUsersModal
        visible={isNotifyUsersModalVisible}
        onClose={() => {
          notifyDefaultRecipientsFetchSeqRef.current += 1;
          setIsNotifyUsersModalVisible(false);
          setRfiToNotify(null);
          setNotifyDefaultUserIds([]);
          setNotifyDefaultUserIdsLoading(false);
        }}
        folder={rfiToNotify}
        itemName={rfiToNotify?.title}
        defaultUserIds={notifyDefaultUserIds}
        defaultUserIdsLoading={notifyDefaultUserIdsLoading}
        onNotify={handleSendNotification}
      />
    </div>
  );
};

export default RFIs;
